import { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import { User, Bell, HelpCircle, X, Trash2, RefreshCw, MoreHorizontal, ChevronLeft, ChevronRight } from "lucide-react"
import { DashboardSidebar } from "@/components/dashboard-sidebar"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { TrashFileList } from "@/components/trash-file-list"
import { NAVIGATION_ROUTES } from '@/constants/routes'
import { clearTrash, deleteTrashItem, getTrashItems, type TrashItem } from '@/api/trash'
import { useToast } from "@/hooks/use-toast"

export default function Trash() {
  const [files, setFiles] = useState<TrashItem[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmType, setConfirmType] = useState<'clearAll' | 'deleteSelected' | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const { toast } = useToast()
  const pageSize = 20

  const totalCount = files.length

  const loadTrash = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await getTrashItems(page, pageSize)
      setFiles(resp.items)
      setTotal(resp.total || 0)
      setTotalPages(resp.totalPages || 1)
      setSelectedIds([])
      setSelectAll(false)
    } catch (err) {
      const msg = (err as Error).message || "加载回收站失败"
      setError(msg)
      toast({
        title: "加载回收站失败",
        description: msg,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, toast])

  useEffect(() => {
    loadTrash()
  }, [loadTrash])

  const handleSelectAll = (checked: boolean) => {
    if (!totalCount) {
      setSelectedIds([])
      setSelectAll(false)
      return
    }
    setSelectAll(checked)
    if (checked) {
      setSelectedIds(files.map((f) => f.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleClearAll = () => {
    if (!totalCount || loading) return
    setConfirmType('clearAll')
  }

  // 自动更新全选状态
  useEffect(() => {
    if (!totalCount) {
      setSelectAll(false)
      return
    }
    if (selectedIds.length === totalCount && totalCount > 0) {
      setSelectAll(true)
    } else {
      setSelectAll(false)
    }
  }, [selectedIds, totalCount])

  const handleDeleteSelected = () => {
    if (!selectedIds.length || loading) return
    setConfirmType('deleteSelected')
  }

  const handleConfirmAction = async () => {
    if (!confirmType) return
    setLoading(true)
    try {
      if (confirmType === 'clearAll') {
        await clearTrash()
        toast({ title: "回收站已清空", description: "所有回收站文件已彻底清除" })
      } else if (confirmType === 'deleteSelected') {
        for (const id of selectedIds) {
          await deleteTrashItem(id)
        }
        toast({ title: "删除成功", description: `已彻底删除选中的 ${selectedIds.length} 个项目` })
      }
      await loadTrash()
    } catch (err) {
      const msg = (err as Error).message || "操作失败"
      toast({ title: "操作失败", description: msg, variant: "destructive" })
    } finally {
      setLoading(false)
      setConfirmType(null)
    }
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* 左侧边栏 */}
      <DashboardSidebar activePage="trash" />

      {/* 右侧内容区 */}
      <div className="flex-1 flex flex-col">
        {/* 顶部标题栏 */}
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            {/* 左侧标题 */}
            <h1 className="text-xl font-bold text-gray-900">回收站</h1>

            {/* 右侧操作按钮 */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-xs text-gray-600">i</span>
                </div>
                <span>回收站文件保留 30 天</span>
              </div>
              <Button variant="ghost" size="sm" className="text-gray-700 hover:text-gray-900 h-auto p-0 text-sm">
                升级会员
              </Button>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 h-8 w-8">
                <HelpCircle className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 relative h-8 w-8">
                <Bell className="w-4 h-4" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </Button>
              <Link to={NAVIGATION_ROUTES.SETTINGS}>
                <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 h-8 w-8">
                  <User className="w-4 h-4" />
                </Button>
              </Link>
              <Link to={NAVIGATION_ROUTES.HOME}>
                <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* 次级工具栏 */}
        <div className="bg-white border-b border-gray-200 px-8 py-3">
          <div className="flex items-center justify-between">
            {/* 左侧：全选和计数 */}
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={selectAll}
                onCheckedChange={(checked) => handleSelectAll(Boolean(checked))}
                disabled={loading || !totalCount}
              />
              <span className="text-sm text-gray-600">
                {selectedIds.length > 0 ? `已选 ${selectedIds.length} 项` : `共 ${totalCount} 项`}
              </span>
            </div>

            {/* 右侧：一键清空 */}
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-600 hover:text-gray-900 h-8"
              onClick={handleClearAll}
              disabled={loading || !totalCount}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {loading ? "处理中..." : "一键清空"}
            </Button>
          </div>
        </div>

        {/* 主内容区 */}
        <main className="flex-1 bg-gray-50 p-8 relative">
          <div className="w-full">
            {/* 列表 */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* 表头 */}
              <div className="grid grid-cols-[auto_1fr_200px_200px] gap-4 px-6 py-4 border-b border-gray-200 text-sm font-medium text-gray-600 bg-gray-50">
                <div className="w-8"></div>
                <div>文件名称</div>
                <div>删除时间</div>
                <div>清理时间</div>
              </div>

              {/* 列表内容 */}
              {error && (
                <div className="px-6 py-3 text-sm text-red-600 bg-red-50 border-b border-red-100">
                  {error}
                </div>
              )}
              <TrashFileList 
                files={files}
                selectedIds={selectedIds}
                onSelectChange={setSelectedIds}
              />
            </div>

            {/* 分页组件 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white rounded-lg mt-4">
                <div className="text-sm text-gray-600">
                  共 {total} 项，第 {page} / {totalPages} 页
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (page > 1) {
                        setPage(page - 1)
                      }
                    }}
                    disabled={page === 1 || loading}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    上一页
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number
                      if (totalPages <= 5) {
                        pageNum = i + 1
                      } else if (page <= 3) {
                        pageNum = i + 1
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i
                      } else {
                        pageNum = page - 2 + i
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                          disabled={loading}
                          className="w-9"
                        >
                          {pageNum}
                        </Button>
                      )
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (page < totalPages) {
                        setPage(page + 1)
                      }
                    }}
                    disabled={page === totalPages || loading}
                  >
                    下一页
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
            {totalPages <= 1 && total > 0 && (
              <div className="text-center text-sm text-gray-500 py-4">
                共 {total} 项
              </div>
            )}
          </div>

          {/* 底部浮动工具栏 */}
          {selectedIds.length > 0 && (
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50">
              <div className="bg-gray-900 text-white rounded-full shadow-2xl px-6 py-3 flex items-center gap-6">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-white hover:bg-white/20 h-9 w-9"
                  title="刷新"
                  onClick={() => loadTrash()}
                  disabled={loading}
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-white hover:bg-white/20 h-9 w-9"
                  title="删除"
                  onClick={handleDeleteSelected}
                  disabled={loading}
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-white hover:bg-white/20 h-9 w-9"
                  title="更多"
                >
                  <MoreHorizontal className="w-5 h-5" />
                </Button>
              </div>
            </div>
          )}
        </main>

        {/* 规范确认对话框 */}
        <AlertDialog open={!!confirmType} onOpenChange={(open) => { if (!open) setConfirmType(null) }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmType === 'clearAll' ? "清空回收站？" : "彻底删除选中的文件？"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmType === 'clearAll'
                  ? "确定要清空回收站吗？此操作将永久彻底删除回收站中的所有文件，无法撤销与恢复。"
                  : `确定要彻底删除选中的 ${selectedIds.length} 个文件吗？此操作无法撤销与恢复。`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={loading}>取消</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmAction}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? "处理中..." : "确认永久删除"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
